import React from "react";
import { Box, IconButton, Skeleton, Typography, useTheme } from "@mui/material";
import { DeleteOutlineRounded } from "@mui/icons-material";
import { WatchlistHolding } from "./hooks/useWatchlists";
import { useCompanyProfile } from "../Charts/hooks/useCompanyProfile";
import { CompanyMark } from "../../Chat/CompanyMark";

interface WatchlistStockItemProps {
  holding: WatchlistHolding;
  onRemove: (holding: WatchlistHolding) => void;
  isRemoving?: boolean;
  /** Hairline rule above the row; omitted on the first so the list opens flush. */
  showDivider?: boolean;
}

const WatchlistStockItem: React.FC<WatchlistStockItemProps> = ({
  holding,
  onRemove,
  isRemoving = false,
  showDivider = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const hairline = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)";

  const { data: companyData, isLoading } = useCompanyProfile(holding.ticker);
  const profile = Array.isArray(companyData) ? companyData[0] : companyData;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        minHeight: 52,
        // Bleeds the hover tint past the list's padding so the row reads as
        // part of the list rather than a card sitting inside it.
        mx: -1,
        px: 1,
        borderRadius: 1.5,
        borderTop: showDivider ? `1px solid ${hairline}` : "none",
        opacity: isRemoving ? 0.4 : 1,
        transition: "background-color 0.18s ease, opacity 0.18s ease",
        "&:hover": {
          backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
        },
        "&:hover .watchlistRemove": { opacity: 1 },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      {/* No plate, no border — the provider's artwork clipped to the app's
          corner radius, matching how logos render across the newer surfaces. */}
      <Box sx={{ flex: "none", display: "flex" }}>
        {isLoading && (
          <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: "5px" }} />
        )}
        {!isLoading && <CompanyMark symbol={holding.ticker} size={24} radius="5px" />}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 1.25 }}>
        <Typography
          sx={{
            flex: "none",
            fontSize: "0.85rem",
            fontWeight: 600,
            letterSpacing: "0.01em",
            color: theme.palette.text.primary,
          }}
        >
          {holding.ticker}
        </Typography>

        {isLoading && <Skeleton width="45%" height={14} />}
        {!isLoading && (
          <Typography
            noWrap
            sx={{ fontSize: "0.8rem", color: theme.palette.text.secondary, minWidth: 0 }}
          >
            {profile?.companyName}
          </Typography>
        )}
      </Box>

      <IconButton
        className="watchlistRemove"
        size="small"
        onClick={() => onRemove(holding)}
        disabled={isRemoving}
        aria-label={`Remove ${holding.ticker}`}
        sx={{
          flex: "none",
          width: 32,
          height: 32,
          color: theme.palette.text.secondary,
          // Understated until wanted on pointer; always visible on touch,
          // where there is no hover to reveal it.
          opacity: 0.5,
          transition: "opacity 0.18s ease, color 0.18s ease",
          "@media (hover: none)": { opacity: 1 },
          "&:hover": {
            opacity: 1,
            color: theme.palette.error.main,
            backgroundColor: "rgba(239, 68, 68, 0.1)",
          },
          "&:focus-visible": { opacity: 1, outline: "2px solid #22c55e", outlineOffset: -2 },
          "&:disabled": { opacity: 0.25 },
        }}
      >
        <DeleteOutlineRounded sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

export default WatchlistStockItem;
